import React, { useMemo, useRef } from 'react'
import { Paper, Popper } from '@mui/material'
import { useStyles } from './TagCell'
import { GridAlignment, GridRenderCellParams } from '@mui/x-data-grid-pro'
import Text, { TextColor } from 'particles/Text'
import IconButton from 'particles/Button/IconButton'
import styled, { useTheme } from 'particles/themes/styled'
import Tooltip from 'particles/Tooltip'
import { useNewTranslation } from 'hooks'

export interface StringProps {
    earning: string
    earning_before_adjustment: string
    color?: TextColor
    adjustment_reason?: string
    onClickEdit?: () => void
    isFullText?: boolean
}

interface GridCellExpandProps {
    stringProps?: StringProps
    width?: number
    align?: GridAlignment
}

const EarningAdjustment = React.memo(function GridCellExpand(props: GridCellExpandProps) {
    const { width, stringProps, align } = props
    const wrapper = React.useRef<HTMLDivElement | null>(null)
    const cellDiv = React.useRef<HTMLDivElement | null>(null)
    const cellValue = React.useRef<HTMLDivElement | null>(null)
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
    const classes = useStyles()
    const [showFullCell, setShowFullCell] = React.useState(false)
    const [showPopper, setShowPopper] = React.useState(false)
    const reasonRef = useRef<HTMLDivElement | null>(null)
    const [t] = useNewTranslation()
    const theme = useTheme()
    const contentWidth = !!width && width > 20 ? width - 20 : 0
    const wrapperWidth = wrapper.current?.offsetWidth || contentWidth
    const cellScrollWidth = cellValue.current?.scrollWidth || 0

    const handleMouseEnter = () => {
        const reasonScrollWidth = reasonRef.current?.scrollWidth || 0
        const isOver = wrapperWidth !== 0 ? cellScrollWidth > wrapperWidth || reasonScrollWidth > wrapperWidth : false

        setShowPopper(isOver)
        setAnchorEl(cellDiv.current)
        setShowFullCell(true)
    }

    const handleMouseLeave = () => {
        setShowFullCell(false)
        setShowPopper(false)
    }

    React.useEffect(() => {
        if (!showFullCell) {
            return undefined
        }

        function handleKeyDown(nativeEvent: KeyboardEvent) {
            // IE11, Edge (prior to using Bink?) use 'Esc'
            if (nativeEvent.key === 'Escape' || nativeEvent.key === 'Esc') {
                setShowFullCell(false)
            }
        }

        document.addEventListener('keydown', handleKeyDown)

        return () => {
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [setShowFullCell, showFullCell])

    const boxStyle = useMemo<React.CSSProperties>(() => {
        let justifyContent

        if (align === 'right') {
            justifyContent = 'flex-end'
        } else if (align === 'center') {
            justifyContent = 'center'
        }

        return { justifyContent }
    }, [align])

    const showOldEarning =
        stringProps?.earning_before_adjustment && stringProps.earning_before_adjustment !== stringProps.earning

    return (
        <div
            ref={wrapper}
            style={boxStyle}
            className={classes.root}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <div
                ref={cellDiv}
                style={{
                    height: 1,
                    width,
                    display: 'block',
                    position: 'absolute',
                    alignItems: 'center',
                    gap: '4px',
                    justifyContent: 'center',
                    top: 0,
                }}
            />

            <StyledAmountContainer
                ref={cellValue}
                amountFlexDirection={cellScrollWidth > wrapperWidth ? 'column' : 'row'}
            >
                <div className="top-section">
                    <div className="amount">
                        <Text
                            color={
                                stringProps && stringProps.color
                                    ? stringProps.color
                                    : stringProps?.earning
                                      ? 'primary'
                                      : 'placeholder'
                            }
                            scale="bodyM500"
                            className="cellValue"
                        >
                            {stringProps?.earning ?? '-'}
                        </Text>
                        <Text color="disable" scale="bodyM500" className="cellValue">
                            {showOldEarning ? `(${stringProps.earning_before_adjustment})` : ''}
                        </Text>
                    </div>
                    {showFullCell && !!stringProps?.onClickEdit ? (
                        <Tooltip label={t('submission.table.totalEarningsLabel.cellButton.tooltips')}>
                            <IconButton color="neutral" icon="pencilLine" size="xs" onClick={stringProps.onClickEdit} />
                        </Tooltip>
                    ) : (
                        <div className="empty-button" />
                    )}
                </div>
                <Text
                    ref={reasonRef}
                    color="disable"
                    style={{
                        textOverflow: 'ellipsis',
                        whiteSpace: stringProps?.isFullText ? 'initial' : 'nowrap',
                        wordBreak: 'break-word',
                        overflow: 'hidden',
                        display: 'inline-block',
                        width: '100%',
                    }}
                >
                    {stringProps?.adjustment_reason}
                </Text>
            </StyledAmountContainer>

            {showPopper && (
                <Popper
                    open={showFullCell && anchorEl !== null}
                    anchorEl={anchorEl}
                    style={{ width, marginLeft: -17, zIndex: 200 }}
                >
                    <Paper
                        elevation={1}
                        style={{
                            minHeight: wrapper.current!.offsetHeight - 3,
                            background: theme.colors.surface.primary,
                            width: 'fit-content',
                            maxWidth: 300,
                            minWidth: width,
                            padding: 8,
                            boxShadow:
                                '0px 5px 5px -3px rgba(0, 0, 0, 0.2), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12)',
                        }}
                    >
                        <StyledAmountContainer amountFlexDirection="row">
                            <div className="top-section">
                                <div className="amount">
                                    <Text
                                        color={
                                            stringProps && stringProps.color
                                                ? stringProps.color
                                                : stringProps?.earning
                                                  ? 'primary'
                                                  : 'placeholder'
                                        }
                                        scale="bodyM500"
                                        className="cellValue"
                                        style={{
                                            textOverflow: 'ellipsis',
                                            whiteSpace: stringProps?.isFullText ? 'initial' : 'nowrap',
                                            wordBreak: 'break-word',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        {stringProps?.earning ?? '-'}
                                    </Text>
                                    <Text
                                        color="disable"
                                        scale="bodyM500"
                                        className="cellValue"
                                        style={{
                                            textOverflow: 'ellipsis',
                                            whiteSpace: stringProps?.isFullText ? 'initial' : 'nowrap',
                                            wordBreak: 'break-word',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        {showOldEarning ? `(${stringProps.earning_before_adjustment})` : ''}
                                    </Text>
                                </div>
                                {showFullCell && !!stringProps?.onClickEdit ? (
                                    <Tooltip label={t('submission.table.totalEarningsLabel.cellButton.tooltips')}>
                                        <IconButton
                                            color="neutral"
                                            icon="pencilLine"
                                            size="xs"
                                            onClick={stringProps.onClickEdit}
                                        />
                                    </Tooltip>
                                ) : (
                                    <div className="empty-button" />
                                )}
                            </div>
                            <Text color="disable">{stringProps?.adjustment_reason}</Text>
                        </StyledAmountContainer>
                    </Paper>
                </Popper>
            )}
        </div>
    )
})

export const EarningAdjustmentCell = (params: GridRenderCellParams) => {
    const stringProps: StringProps = params.value as StringProps
    return <EarningAdjustment stringProps={stringProps} width={params.colDef.width} align={params.colDef.align} />
}

interface StyledAmountContainerProps {
    amountFlexDirection: string
}

const StyledAmountContainer = styled.div<StyledAmountContainerProps>`
    width: fit-content;
    min-width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    overflow: hidden;

    .empty-button {
        width: 20px;
    }

    .top-section {
        justify-content: space-between;
        align-items: center;
        display: flex;
        width: fit-content;
        min-width: 100%;

        .amount {
            display: flex;
            gap: 4px;
            flex-direction: ${({ amountFlexDirection }) => amountFlexDirection};
        }
    }
`
